// src/reports/Expenses.js
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../pages/supabaseClient";
import { saveAs } from "file-saver";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";

/* -------- helpers -------- */
const dkey = (d) =>
  !d ? "" : typeof d === "string" ? d.slice(0, 10) : format(new Date(d), "yyyy-MM-dd");
const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/* -------- table name -------- */
const TBL = { EXPENSES: "expenses" };

export default function Expenses({ fromDate, toDate }) {
  const [viewType, setViewType] = useState("daily"); // daily | weekly | monthly
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [staffFilter, setStaffFilter] = useState("All"); // for Paid to Staff rows
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [categories, setCategories] = useState(["All"]);
  const [staffNames, setStaffNames] = useState(["All"]);

  useEffect(() => {
    if (fromDate && toDate) fetchData();
  }, [fromDate, toDate, viewType, categoryFilter, staffFilter]);

  async function fetchData() {
    setLoading(true);
    try {
      // only select columns that actually exist (per your schema)
      const { data, error } = await supabase
        .from(TBL.EXPENSES)
        .select("id,date,category,staff_name,description,amount")
        .gte("date", fromDate)
        .lte("date", toDate)
        .order("date", { ascending: true });

      if (error) throw error;

      // Build filters
      const catSet = new Set(["All"]);
      const staffSet = new Set(["All"]); // only meaningful for Paid to Staff
      (data || []).forEach((r) => {
        if (r.category) catSet.add(r.category);
        if ((r.category || "").toLowerCase() === "paid to staff" && r.staff_name) {
          staffSet.add(r.staff_name);
        }
      });
      setCategories(Array.from(catSet));
      setStaffNames(Array.from(staffSet));

      // Apply filters
      const filtered = (data || []).filter((r) => {
        const catOK = categoryFilter === "All" || r.category === categoryFilter;
        const staffOK =
          staffFilter === "All" ||
          ((r.category || "").toLowerCase() === "paid to staff" &&
            (r.staff_name || "").trim().toLowerCase() === staffFilter.trim().toLowerCase());
        return catOK && staffOK;
      });

      // Build view-specific rows
      if (viewType === "daily") {
        // show each expense as a row
        const out = filtered.map((r) => ({
          id: r.id,
          date: dkey(r.date),
          category: r.category || "",
          staff: (r.category || "").toLowerCase() === "paid to staff" ? r.staff_name || "" : "",
          notes: r.description || "",
          amount: n(r.amount),
        }));
        setRows(out);
      } else {
        // weekly or monthly aggregation
        const grouped = {};
        filtered.forEach((r) => {
          const dateStr = dkey(r.date);
          const bucket =
            viewType === "weekly"
              ? format(parseISO(dateStr), "RRRR-'W'II")
              : dateStr.slice(0, 7); // YYYY-MM

          const isPaid = (r.category || "").toLowerCase() === "paid to staff";
          const key = isPaid
            ? `${bucket}|${r.category}|${r.staff_name || ""}`
            : `${bucket}|${r.category}`;

          if (!grouped[key]) {
            grouped[key] = {
              bucket,
              category: r.category || "",
              staff: isPaid ? r.staff_name || "" : "",
              amount: 0,
              count: 0,
            };
          }
          grouped[key].amount += n(r.amount);
          grouped[key].count += 1;
        });

        setRows(Object.values(grouped).sort((a, b) => a.bucket.localeCompare(b.bucket)));
      }
    } catch (err) {
      console.error("Expenses fetch error:", err);
      toast.error("Expenses fetch error: " + err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  /* -------- totals -------- */
  const totals = useMemo(() => {
    if (!rows.length) return { amount: 0, count: 0 };
    if (viewType === "daily") {
      return rows.reduce(
        (a, r) => ({ amount: a.amount + r.amount, count: a.count + 1 }),
        { amount: 0, count: 0 }
      );
    }
    // weekly/monthly
    return rows.reduce(
      (a, r) => ({ amount: a.amount + r.amount, count: a.count + r.count }),
      { amount: 0, count: 0 }
    );
  }, [rows, viewType]);

  /* -------- category summary (for the table you’re viewing) -------- */
  const categorySummary = useMemo(() => {
    const map = new Map();
    if (viewType === "daily") {
      rows.forEach((r) => map.set(r.category, (map.get(r.category) || 0) + r.amount));
    } else {
      rows.forEach((r) => map.set(r.category, (map.get(r.category) || 0) + r.amount));
    }
    return Array.from(map.entries())
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [rows, viewType]);

  /* -------- CSV -------- */
  const exportCSV = () => {
    if (!rows.length) return;
    const lines =
      viewType === "daily"
        ? [
            ["Date", "Category", "Staff (Paid to Staff)", "Notes", "Amount (£)"].join(","),
            ...rows.map((r) =>
              [
                r.date,
                r.category,
                r.staff,
                (r.notes || "").replace(/[\r\n,]+/g, " "),
                r.amount.toFixed(2),
              ].join(",")
            ),
          ]
        : [
            ["Bucket", "Category", "Staff (Paid to Staff)", "Count", "Amount (£)"].join(","),
            ...rows.map((r) => [r.bucket, r.category, r.staff || "", r.count, r.amount.toFixed(2)].join(",")),
          ];

    saveAs(
      new Blob([lines.join("\n")], { type: "text/csv" }),
      `expenses_${fromDate}_${toDate}_${viewType}.csv`
    );
  };

  /* -------- styles -------- */
  const th = { background: "#F5F5F5", fontWeight: 700 };
  const badge = (text, color, bg) => (
    <span
      style={{
        display: "inline-block",
        padding: "2px 6px",
        borderRadius: 6,
        fontSize: 12,
        color,
        background: bg,
      }}
    >
      {text}
    </span>
  );

  return (
    <div>
      {/* Controls */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <button onClick={() => setViewType("daily")} disabled={loading || viewType === "daily"}>
            Daily
          </button>
          <button onClick={() => setViewType("weekly")} disabled={loading || viewType === "weekly"}>
            Weekly
          </button>
          <button onClick={() => setViewType("monthly")} disabled={loading || viewType === "monthly"}>
            Monthly
          </button>
        </div>

        <label>
          Category:&nbsp;
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} disabled={loading}>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        <label>
          Staff (Paid to Staff):&nbsp;
          <select value={staffFilter} onChange={(e) => setStaffFilter(e.target.value)} disabled={loading}>
            {staffNames.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        <button onClick={exportCSV} disabled={!rows.length}>
          Export CSV
        </button>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : !rows.length ? (
        <p>No expenses in this range.</p>
      ) : viewType === "daily" ? (
        <>
          <table>
            <thead>
              <tr>
                <th style={th}>Date</th>
                <th style={th}>Category</th>
                <th style={th}>Staff (Paid to Staff)</th>
                <th style={th}>Notes</th>
                <th style={th}>Amount (£)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.date}</td>
                  <td>
                    {r.category}
                    {(r.category || "").toLowerCase() === "paid to staff" &&
                      " " &&
                      badge("Payroll", "#B71C1C", "#FFEBEE")}
                  </td>
                  <td>{r.staff || "-"}</td>
                  <td>{r.notes || "-"}</td>
                  <td style={{ fontWeight: 600 }}>{r.amount.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#FAFAFA", fontWeight: 700 }}>
                <td>Totals</td>
                <td />
                <td />
                <td />
                <td>{totals.amount.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>

          {/* Category totals summary */}
          <div style={{ marginTop: 12 }}>
            <strong>Category Totals:</strong>
            <table>
              <thead>
                <tr>
                  <th style={th}>Category</th>
                  <th style={th}>Amount (£)</th>
                </tr>
              </thead>
              <tbody>
                {categorySummary.map((c) => (
                  <tr key={c.category}>
                    <td>
                      {c.category}
                      {(c.category || "").toLowerCase() === "paid to staff" &&
                        " " &&
                        badge("Payroll", "#B71C1C", "#FFEBEE")}
                    </td>
                    <td style={{ fontWeight: 600 }}>{c.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        // weekly or monthly view
        <>
          <table>
            <thead>
              <tr>
                <th style={th}>{viewType === "weekly" ? "Week" : "Month"}</th>
                <th style={th}>Category</th>
                <th style={th}>Staff (Paid to Staff)</th>
                <th style={th}>Count</th>
                <th style={th}>Amount (£)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.bucket}_${r.category}_${r.staff}_${i}`}>
                  <td>{r.bucket}</td>
                  <td>
                    {r.category}
                    {(r.category || "").toLowerCase() === "paid to staff" &&
                      " " &&
                      badge("Payroll", "#B71C1C", "#FFEBEE")}
                  </td>
                  <td>{r.staff || "-"}</td>
                  <td>{r.count}</td>
                  <td style={{ fontWeight: 600 }}>{r.amount.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "#FAFAFA", fontWeight: 700 }}>
                <td>Totals</td>
                <td />
                <td />
                <td>{totals.count}</td>
                <td>{totals.amount.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>

          {/* Category totals summary */}
          <div style={{ marginTop: 12 }}>
            <strong>Category Totals:</strong>
            <table>
              <thead>
                <tr>
                  <th style={th}>Category</th>
                  <th style={th}>Amount (£)</th>
                </tr>
              </thead>
              <tbody>
                {categorySummary.map((c) => (
                  <tr key={c.category}>
                    <td>
                      {c.category}
                      {(c.category || "").toLowerCase() === "paid to staff" &&
                        " " &&
                        badge("Payroll", "#B71C1C", "#FFEBEE")}
                    </td>
                    <td style={{ fontWeight: 600 }}>{c.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
